<script setup>
import { onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/authStore'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const statusText = ref('กำลังเชื่อมต่อข้อมูลกับ LINE...')
const isLoading = ref(true)
const errorMessage = ref('')

let hasCalled = false

onMounted(async () => {
  if (hasCalled) return
  hasCalled = true
  try {
    const { code, state, error, error_description } = route.query
    
    if (error) {
      throw new Error(error_description || 'ถูกยกเลิก หรือไม่ได้รับอนุญาตให้ใช้ LINE Login')
    }

    if (!code) {
      throw new Error('ไม่พบข้อมูล Authorization Code')
    }

    // Verify CSRF state
    const savedState = localStorage.getItem('line_oauth_state')
    localStorage.removeItem('line_oauth_state')
    
    if (savedState && state !== savedState) {
       console.warn('State mismatch. Possible CSRF attack.')
    }

    const redirectUri = `${window.location.origin}/auth/line/callback`

    const res = await fetch('/api/users/auth/line', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirectUri })
    })

    const data = await res.json()

    if (!data.success) {
      throw new Error(data.error || 'การยืนยันตัวตนล้มเหลว')
    }

    // Success login
    authStore.login(data.data, data.data.token)
    statusText.value = 'เข้าสู่ระบบสำเร็จ! กำลังพาท่านกลับสู่หน้าหลัก...'
    
    setTimeout(() => {
        router.push('/')
    }, 1500)

  } catch(err) {
    statusText.value = ''
    errorMessage.value = err.message || 'เกิดข้อผิดพลาดจากระบบ'
    isLoading.value = false
  }
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div class="bg-white w-full max-w-md rounded-3xl p-8 shadow-xl text-center border border-gray-100">
      
      <!-- Logo Display -->
      <div class="flex items-center justify-center gap-4 mb-8">
          <div class="w-16 h-16 bg-[#06C755] rounded-2xl flex items-center justify-center shadow-lg">
              <svg class="h-10 w-10 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M 19.365 9.863 c 0.349 0 0.63 0.285 0.63 0.631 0 0.345 -0.281 0.63 -0.63 0.63 H 17.61 v 1.125 h 1.755 c 0.349 0 0.63 0.283 0.63 0.63 0 0.344 -0.281 0.629 -0.63 0.629 h -2.386 c -0.345 0 -0.627 -0.285 -0.627 -0.629 V 8.108 c 0 -0.345 0.282 -0.63 0.627 -0.63 h 2.386 c 0.346 0 0.627 0.285 0.627 0.63 0 0.349 -0.281 0.63 -0.63 0.63 H 17.61 v 1.125 h 1.755 z m -3.855 3.016 c 0 0.27 -0.174 0.51 -0.432 0.596 -0.064 0.021 -0.133 0.031 -0.199 0.031 -0.211 0 -0.391 -0.09 -0.51 -0.25 l -2.443 -3.317 v 2.94 c 0 0.344 -0.279 0.629 -0.631 0.629 -0.346 0 -0.626 -0.285 -0.626 -0.629 V 8.108 c 0 -0.27 0.173 -0.51 0.43 -0.595 0.06 -0.023 0.136 -0.033 0.194 -0.033 c 0.195 0 0.375 0.104 0.495 0.254 l 2.462 3.33 V 8.108 c 0 -0.345 0.282 -0.63 0.63 -0.63 c 0.345 0 0.63 0.285 0.63 0.63 v 4.771 z m -5.741 0 c 0 0.344 -0.282 0.629 -0.631 0.629 c -0.345 0 -0.627 -0.285 -0.627 -0.629 V 8.108 c 0 -0.345 0.282 -0.63 0.627 -0.63 c 0.346 0 0.628 0.285 0.628 0.63 v 4.771 z m -2.466 0.629 H 4.917 c -0.345 0 -0.63 -0.285 -0.63 -0.629 V 8.108 c 0 -0.345 0.285 -0.63 0.63 -0.63 c 0.348 0 0.63 0.285 0.63 0.63 v 4.141 h 1.756 c 0.348 0 0.629 0.283 0.629 0.63 0 0.344 -0.282 0.629 -0.629 0.629 M 24 10.314 C 24 4.943 18.615 0.572 12 0.572 S 0 4.943 0 10.314 c 0 4.811 4.27 8.842 10.035 9.608 c 0.391 0.082 0.923 0.258 1.058 0.59 c 0.12 0.301 0.079 0.766 0.038 1.08 l -0.164 1.02 c -0.045 0.301 -0.24 1.186 1.049 0.645 c 1.291 -0.539 6.916 -4.078 9.436 -6.975 C 23.176 14.393 24 12.458 24 10.314"/></svg>
          </div>
          <div class="h-1 w-12 bg-gray-200 mx-2 relative overflow-hidden rounded-full">
              <div v-if="isLoading" class="absolute inset-0 bg-[#06C755] w-full animate-pulse"></div>
          </div>
          <div class="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg text-white font-black text-2xl">
              M
          </div>
      </div>

      <h1 class="text-2xl font-black text-gray-900 mb-2">LINE Login</h1>
      
      <div v-if="isLoading" class="space-y-4">
          <svg class="animate-spin h-10 w-10 text-[#06C755] mx-auto" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-gray-600">{{ statusText }}</p>
      </div>

      <div v-if="!isLoading && errorMessage" class="space-y-6">
          <div class="p-4 bg-red-50 border border-red-200 rounded-xl">
              <h3 class="font-bold text-red-800 mb-1">เกิดข้อผิดพลาด</h3>
              <p class="text-red-600 text-sm">{{ errorMessage }}</p>
          </div>
          <button @click="router.push('/')" class="w-full bg-gray-900 text-white font-bold py-3.5 px-4 rounded-xl transition-all shadow-md mt-4 hover:shadow-lg hover:-translate-y-0.5">
              กลับสู่หน้าหลัก
          </button>
      </div>
      
    </div>
  </div>
</template>
